import { useId } from 'react';
import clsx from 'clsx/lite';
import { delay, iconClassName, styles, svgProps } from '@/components/service-showcase/icons/shared';
import type { IServiceShowcaseSvgIconProps } from '@/components/service-showcase/icons/type';

const PRODUCT_PATHS = ['M86 110 Q170 70 250 66', 'M86 110 Q175 105 262 105', 'M86 110 Q170 150 250 154'];

const PRODUCT_BARS = [
  { x: 250, y: 58, filled: false, so: 0.55 },
  { x: 262, y: 97, filled: true, so: 0.7 },
  { x: 250, y: 146, filled: false, so: 0.55 },
];

// Two stub lines inside each bar suggesting data fields
const DATA_LINES = [
  { x1: 256, y1: 63, x2: 300, y2: 63, d: 0.74 },
  { x1: 256, y1: 69, x2: 278, y2: 69, d: 0.77 },
  { x1: 268, y1: 102, x2: 312, y2: 102, d: 0.86 },
  { x1: 268, y1: 108, x2: 290, y2: 108, d: 0.89 },
  { x1: 256, y1: 151, x2: 298, y2: 151, d: 0.98 },
  { x1: 256, y1: 157, x2: 274, y2: 157, d: 1.01 },
];

// 10 s cycle. Hold window: ~2.3 s → 7.3 s (last element drawn by ~3.3 s).
// Each packet travels for 1.2 s = 12 % of the 10 s cycle (keyTimes 0.01→0.13).
// Round 1 departs 3.4–3.9 s; round 2 departs 5.2–5.7 s (1.8 s gap so r1 fully
// arrives before r2 departs). Both rounds finish well before draw-out at ~7.3 s.
const PACKETS = [
  { pathIdx: 0, begin: '3.4s' },
  { pathIdx: 1, begin: '3.65s' },
  { pathIdx: 2, begin: '3.9s' },
  { pathIdx: 0, begin: '5.2s' },
  { pathIdx: 1, begin: '5.45s' },
  { pathIdx: 2, begin: '5.7s' },
];

export default function ProductFlowIcon(props: IServiceShowcaseSvgIconProps) {
  const { active = true, className, ...rest } = props;
  const glowId = useId();
  const pathPrefix = useId();

  return (
    <svg {...svgProps} {...rest} className={iconClassName(active, clsx(styles.product, className))}>
      <defs>
        <radialGradient id={glowId} cx='28%' cy='50%' r='55%'>
          <stop offset='0%' stopColor='currentColor' stopOpacity='0.32' />
          <stop offset='100%' stopColor='currentColor' stopOpacity='0' />
        </radialGradient>
        {/* Invisible path definitions referenced by animateMotion mpath */}
        {PRODUCT_PATHS.map((d, i) => (
          <path key={i} id={`${pathPrefix}p${i}`} d={d} />
        ))}
      </defs>

      <circle cx='86' cy='110' r='44' fill={`url(#${glowId})`} />

      {/* Flow paths */}
      {PRODUCT_PATHS.map((path, i) => (
        <path
          key={path} d={path}
          stroke='currentColor' strokeWidth='1.2'
          strokeOpacity={[0.5, 0.55, 0.5][i]}
          className={styles.link}
          style={delay(`${0.25 + i * 0.15}s`)}
          pathLength='1'
        />
      ))}

      {/* Source dot */}
      <circle cx='86' cy='110' r='5' fill='currentColor'
        className={styles.pop} style={delay('0.1s')}
      />

      {/* Destination bars */}
      {PRODUCT_BARS.map(({ x, y, filled, so }, i) => (
        <rect key={i}
          x={x} y={y} width='60' height='16' rx='3'
          fill={filled ? 'currentColor' : 'none'}
          fillOpacity={filled ? 0.1 : 0}
          stroke='currentColor' strokeOpacity={so} strokeWidth='1.1'
          className={styles.pop}
          style={delay(`${0.7 + i * 0.12}s`)}
        />
      ))}

      {/* Data field stubs inside each bar */}
      {DATA_LINES.map(({ x1, y1, x2, y2, d }) => (
        <line key={`${x1}-${y1}`}
          x1={x1} y1={y1} x2={x2} y2={y2}
          stroke='currentColor' strokeOpacity='0.38' strokeWidth='1'
          className={styles.pop} style={delay(`${d}s`)}
        />
      ))}

      {/* Data packets flying along each path */}
      {PACKETS.map(({ pathIdx, begin }) => (
        <circle key={`${pathIdx}-${begin}`} r='2.5' fill='currentColor'>
          <animateMotion
            dur='10s' repeatCount='indefinite' begin={begin}
            keyPoints='0;0;1;1' keyTimes='0;0.01;0.13;1'
            calcMode='linear'
          >
            <mpath href={`#${pathPrefix}p${pathIdx}`} />
          </animateMotion>
          <animate
            attributeName='opacity'
            values='0;1;1;0;0'
            keyTimes='0;0.01;0.13;0.15;1'
            dur='10s' repeatCount='indefinite' begin={begin}
          />
        </circle>
      ))}
    </svg>
  );
}
